import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";

// MUI imports
import Grid from "@mui/material/Grid";

// Project imports
import TextFieldTR from "../FormComponents/TextFieldTR";
import SelectTR from "../FormComponents/SelectTR";
import ButtonTR from "../FormComponents/ButtonTR";
import MultipleSelectTR from "../FormComponents/MultipleSelectTR";
import { TrainingCentreRegContext } from "../helperFunctions";
import {
  saveFormDataPartOne,
  nextFormStep,
  closePartOnePopup,
} from "../../../Actions";
import RegFormPopup from "./RegFormPopup";

const partOneMockData = {
  trainingTypes: [
    {
      label: "Hands-on Skill Trainings",
      value: 1,
    },
    {
      label: "Entrepreneurship Development Programme",
      value: 2,
    },
    {
      label: "Employability Skills",
      value: 3,
    },
  ],
};

const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const formValidationSchema = Yup.object().shape({
  trainingTypeId: Yup.string().required("This is a required field"),
  skillCenterName: Yup.string().required("This is a required field"),
  centerEmail: Yup.string()
    .matches(EMAIL_REGEX, "Invalid email address")
    .required("This is a required field"),
  centerPhoneNumber: Yup.number()
    .integer()
    .min(100000000, "Phone number must be at least 10 digits")
    .required("This is a required field")
    .typeError("Please enter a valid phone number"),
  centerWhatsappNumber: Yup.number()
    .required("This is a required field")
    .integer()
    .min(100000000, "Phone number must be at least 10 digits")
    .typeError("Please enter a valid phone number"),
  stateId: Yup.string().required("This is a required field"),
  localGovernmentId: Yup.string().required("This is a required field"),
  physicalAddress: Yup.string().required("This is a required field"),
  closeLandMark: Yup.string().required("This is a required field"),
  skillIds: Yup.array(Yup.number()).min(
    1,
    "Select at least one skill taught at your centre"
  ),
});

// component that watches for a change in state
// and updates the lga list accordingly
const GenerateParsedLGAList = ({ setCurLGA }) => {
  const tregReducer = (globalState) => globalState.trainingCentreRegReducer;

  const { values } = useFormikContext();
  const { listOfStates } = useSelector(tregReducer);

  // generate LGA for chosen state
  useEffect(() => {
    if (typeof values.stateId === "number") {
      const foundState = listOfStates.find((el) => el.value === values.stateId);
      setCurLGA(foundState.lgas);
    }
  }, [values]);

  return null;
};

// main component
const PartOne = () => {
  const globalDispatch = useDispatch();
  const tregReducer = (globalState) => globalState.trainingCentreRegReducer;
  const contextObject = useContext(TrainingCentreRegContext);
  const {
    listOfStates,
    partOnePopupOpen,

    // form field states from reducer
    trainingTypeId,
    skillIds,
    skillCenterName,
    centerEmail,
    centerPhoneNumber,
    centerWhatsappNumber,
    stateId,
    localGovernmentId,
    physicalAddress,
    closeLandMark,
  } = useSelector(tregReducer);

  const [curLGA, setCurLGA] = useState([]);
  const [initialState ] = useState({
    trainingTypeId,
    skillIds,
    skillCenterName,
    centerEmail,
    centerPhoneNumber,
    centerWhatsappNumber,
    stateId,
    localGovernmentId,
    physicalAddress,
    closeLandMark,
  });

  function handleStepSubmit(values) {
    globalDispatch(saveFormDataPartOne(values));
    globalDispatch(nextFormStep());
  }

  function closePopup() {
    globalDispatch(closePartOnePopup());
  }

  // markups
  const sectionsOneToTwoMarkup = [
    {
      type: "singleSelect",
      name: "trainingTypeId",
      label:
        "What type of training is carried out in your centre? (Select one)*",
      options: partOneMockData.trainingTypes,
    },
    {
      type: "multipleSelect",
      label: "List the skills taught in your centre*",
      name: "skillIds",
      options: contextObject.parsedListOfSkills,
    },
  ];

  const sectionThreeMarkup = [
    [
      {
        type: "textField",
        label: "Name of Centre*",
        name: "skillCenterName",
      },
      {
        type: "textField",
        label: "Centre Email Address*",
        name: "centerEmail",
      },
    ],
    [
      {
        type: "textField",
        label: "Phone Number*",
        name: "centerPhoneNumber",
      },
      {
        type: "textField",
        label: "WhatsApp Number*",
        name: "centerWhatsappNumber",
      },
    ],
    [
      {
        type: "singleSelect",
        label: "State*",
        name: "stateId",
        options: listOfStates,
      },
      {
        type: "singleSelect",
        name: "localGovernmentId",
        label: "L.G.A*",
        options: curLGA,
      },
    ],
    [
      {
        type: "textField",
        label: "Full Address*",
        name: "physicalAddress",
      },
      {
        type: "textField",
        label: "Landmark(s) closest to you*",
        name: "closeLandMark",
      },
    ],
  ];

  useEffect(() => {
    const element = document.getElementById("treg-container");
    element.scrollIntoView({ behavior: "smooth" });
  }, [])

  return (
    <>
      <Formik
        initialValues={{
          ...initialState,
        }}
        validationSchema={formValidationSchema}
        onSubmit={handleStepSubmit}
      >
        <Form>
          <h2>Sections 1 - 2 </h2>
          {sectionsOneToTwoMarkup.map((field) => (
            <Grid item xs key={field.label}>
              {field.type === "singleSelect" && (
                <SelectTR
                  name={field.name}
                  label={field.label}
                  options={field.options}
                />
              )}

              {field.type === "multipleSelect" && (
                <MultipleSelectTR
                  label={field.label}
                  name={field.name}
                  options={field.options}
                />
              )}
            </Grid>
          ))}

          <h2 style={{ marginTop: "24px" }}>Section 3</h2>
          <p>a. Biodata of Training Centre</p>

          {sectionThreeMarkup.map((row, index) => (
            <Grid
              key={index}
              sx={{
                display: "flex",
                flexDirection: "row",
              }}
              container
              spacing={3}
            >
              {row.map((field, index) => (
                <Grid item xs={12} md={6} key={index}>
                  {field.type === "textField" && (
                    <TextFieldTR
                      label={field.label}
                      name={field.name}
                      // onChange={field.onChange}
                    />
                  )}

                  {field.type === "singleSelect" && (
                    <Grid
                      item
                      xs
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1,
                      }}
                    >
                      <SelectTR
                        name={field.name}
                        label={field.label}
                        options={field.options}
                      />
                    </Grid>
                  )}
                </Grid>
              ))}
            </Grid>
          ))}
          <Grid
            item
            xs
            container
            sx={{ justifyContent: "flex-end", paddingTop: "1rem" }}
          >
            <ButtonTR sx={{ width: "fit-content" }}> Next </ButtonTR>
          </Grid>
          <GenerateParsedLGAList setCurLGA={setCurLGA} />
        </Form>
      </Formik>
      <RegFormPopup open={partOnePopupOpen} handleClose={closePopup} />
    </>
  );
};

export default PartOne;
